import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { PLAYLISTS_QUERY, PLAYLIST_GROUPS_QUERY, GET_PLAYLIST_DETAIL } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
// Direct module paths, NOT barrels — see the note in `useAssetsPrefetch`.
import { resolveItemPageSize, resolveFolderPageSize } from '@/components/shared/FolderItemList/dev';
import {
  buildPlaylistQueryInput,
  buildPlaylistSortInput,
} from '@/features/playlists/utils/build-playlist-query';
import { usePlaylistFilterStore } from '@/features/playlists/stores/playlist-filter-store';

/**
 * Default `pageSize` argument of `usePlaylists`. `PlaylistsPage` calls
 * `usePlaylists(currentPath === '/' ? null : currentPath, folderTeamId)` without overriding it.
 * Duplicated deliberately and covered by a parity test — see `useAssetsPrefetch`.
 */
const PLAYLISTS_DEFAULT_PAGE_SIZE = 50;

/**
 * Idle-time prefetch for the Playlists list. See `useAssetsPrefetch` for the full rationale
 * (cache-key parity, why the builders are reused, why filters can be assumed at defaults).
 *
 * Warms the items query, the root-folders query, and the DETAIL of whichever playlist the page
 * will auto-select. `PlaylistsPage` selects `playlists[0]` on arrival (`:223`) and the selection
 * store is memory-only, so on the hard reload where prefetch matters that detail query always
 * fires — it is not a speculative guess. The id is not knowable up front, so the detail prefetch
 * chains off the list result.
 *
 * The page also fires two favourites-section queries at root (`first: FAVORITES_CAP` = 1000,
 * `usePlaylists.ts:240,260`); those are deliberately NOT prefetched — see #1229 notes.
 *
 * Note `PlaylistsPage` passes `null` (not `'/'`) for the root path, and the resulting variables
 * OMIT `extendParams` entirely — `excludeFavExtend` is hardcoded `false` (`usePlaylists.ts:290`)
 * and `favoritesOnly` defaults false. An `extendParams: {}` here would be a DIFFERENT Apollo
 * cache key and the prefetch would silently miss.
 */
export function usePlaylistsPrefetch(): void {
  const client = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const sortField = usePlaylistFilterStore((s) => s.sortField);
  const sortDirection = usePlaylistFilterStore((s) => s.sortDirection);

  useEffect(() => {
    if (!activeTeamId) return;

    const run = () => {
      const queryInput = buildPlaylistQueryInput(
        { searchText: '', searchScope: 'folder' },
        null,
        undefined,
      );

      client
        .query({
          query: PLAYLISTS_QUERY,
          variables: {
            first: resolveItemPageSize(PLAYLISTS_DEFAULT_PAGE_SIZE),
            teamId: activeTeamId,
            query: queryInput,
            sort: buildPlaylistSortInput(sortField, sortDirection),
            // NO extendParams key — see the doc comment above.
          },
          fetchPolicy: 'cache-first',
        })
        .then((res) => {
          // `usePlaylistDetail` keys on `id` (NOT `_id` — Schedules uses `_id`); mismatching the
          // argument name would warm a different cache entry and silently buy nothing.
          const firstId = (
            res?.data as { playlists?: { page?: { edges?: Array<{ node?: { _id?: string } }> } } }
          )?.playlists?.page?.edges?.[0]?.node?._id;
          if (!firstId) return;
          return client
            .query({
              query: GET_PLAYLIST_DETAIL,
              variables: { id: firstId, teamId: activeTeamId },
              fetchPolicy: 'cache-first',
            })
            .then(() => undefined);
        })
        .catch(() => {
          // Best-effort — don't surface errors to the user.
        });

      client
        .query({
          query: PLAYLIST_GROUPS_QUERY,
          variables: {
            first: resolveFolderPageSize(),
            teamId: activeTeamId,
            // At root, `query.teamId` is omitted and `path` is null (usePlaylists.ts:210).
            query: { type: 'PLAYLIST', path: null },
            sort: { [sortField]: sortDirection },
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      setTimeout(run, 2000);
    }
  }, [client, activeTeamId, sortField, sortDirection]);
}
